import React, { forwardRef } from "react";

/**
 * Wrap arbitrary user-supplied text into (at most) two lines that
 * fit inside a habit-loop slot. Splits on the last whitespace
 * before `maxLen`, and truncates line 2 with a middle-dot ellipsis
 * if the leftover still overflows. Used to display member-written
 * cue/routine/reward text without breaking the diagram layout.
 */
function _wrapTwoLines(text, maxLen = 26) {
  const t = String(text || "").trim();
  if (!t) return { line1: "", line2: "" };
  if (t.length <= maxLen) return { line1: t, line2: "" };
  const cut = t.lastIndexOf(" ", maxLen);
  // No space in the first maxLen chars — hard-cut, no space to break on.
  if (cut === -1) {
    const rest = t.slice(maxLen);
    const line2 = rest.length > maxLen ? rest.slice(0, maxLen - 1) + "…" : rest;
    return { line1: t.slice(0, maxLen), line2 };
  }
  const line1 = t.slice(0, cut);
  const rest = t.slice(cut + 1);
  if (rest.length <= maxLen) return { line1, line2: rest };
  const cut2 = rest.lastIndexOf(" ", maxLen);
  const line2 =
    (cut2 === -1 ? rest.slice(0, maxLen - 1) : rest.slice(0, cut2)) + "…";
  return { line1, line2 };
}

/**
 * Finhabits-branded habit-loop diagram.
 *
 * Pure SVG so it scales crisp at any size + downloads cleanly as PDF.
 * Center "Vision" replaces the textbook "Craving" — our brand frames
 * the loop around what you're building toward, not what you crave.
 *
 * v4 change: accepts optional `cue`, `routine`, `reward` props. When
 * a member has saved their own habit reflection, the Habits page
 * passes those strings in so the diagram (and its downloadable PDF)
 * reflect the member's actual loop instead of the generic
 * lead-magnet placeholder text. `review` intentionally stays fixed
 * — it's the *when*, not the *what*, and everyone's schedule is
 * different (Sunday evening for some, whenever-it-happens for
 * others).
 *
 * v3 change: added the "Review" beat at 9 o'clock. The loop now runs
 * on a full 4-position clock: Cue (12) → Routine (3) → Reward (6) →
 * Review (9) → Cue again. Review is the weekly check-in that turns a
 * one-off routine into a repeatable rhythm.
 *
 * Layout rule (v2, preserved): every text label sits OUTSIDE the ring
 * stroke, and the 4 directional arc-arrows live in the 60° gaps
 * BETWEEN the labels (1:30, 4:30, 7:30, 10:30 clock positions). The
 * ring + arrows never run through any word.
 *
 * Exposes a forwardRef so the download button can grab the underlying
 * <svg> node for rasterization → PDF.
 */
const HabitLoopDiagram = forwardRef(function HabitLoopDiagram(
  { cue, routine, reward } = {}, ref,
) {
  // Wrap member-supplied text (or fall back to lead-magnet defaults).
  const cueLines = cue
    ? _wrapTwoLines(cue)
    : { line1: "The trigger you see —", line2: "payday, bill day, statement" };
  const routineLines = routine
    ? _wrapTwoLines(routine)
    : { line1: "The deposit, the check-off,", line2: "the look at the plan" };
  const rewardLines = reward
    ? _wrapTwoLines(reward)
    : { line1: "The number moves —", line2: "savings up, debt down" };
  // Review is intentionally fixed — the "when", not the "what".
  const reviewLines = {
    line1: "Sunday eve or your time —",
    line2: "did the loop hold?",
  };

  return (
    <svg
      ref={ref}
      viewBox="0 0 600 600"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      role="img"
      aria-label="The Finhabits Habit Loop: Cue, Routine, Reward, Review — with the vision of Financial Success at the center."
      data-testid="habit-loop-diagram"
    >
      {/* Solid cream background — keeps PDF on-brand even when printed */}
      <rect width="600" height="600" fill="#F7F5F0" />

      {/* Title */}
      <text
        x="300" y="46"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="34"
        fontWeight="600"
        fill="#2C302E"
      >
        The Habit Loop
      </text>
      <text
        x="300" y="68"
        textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10"
        letterSpacing="3"
        fill="#607762"
      >
        FINHABITS · BETTER HABITS · BETTER MONEY · BETTER LIFE
      </text>

      {/* Outer ring */}
      <circle
        cx="300" cy="330" r="135"
        fill="none"
        stroke="#3E6B37"
        strokeWidth="4"
      />

      {/* Arrow head marker, reused by all 4 arc arrows */}
      <defs>
        <marker
          id="arrow-olive"
          viewBox="0 0 10 10"
          refX="6" refY="5"
          markerWidth="6" markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="#3E6B37" />
        </marker>
      </defs>

      {/* Top-right arc (1:30): Cue → Routine. */}
      <path
        d="M 335 200 A 135 135 0 0 1 430 295"
        fill="none"
        stroke="#3E6B37"
        strokeWidth="3"
        markerEnd="url(#arrow-olive)"
        opacity="0.75"
      />
      {/* Bottom-right arc (4:30): Routine → Reward. */}
      <path
        d="M 430 365 A 135 135 0 0 1 335 460"
        fill="none"
        stroke="#3E6B37"
        strokeWidth="3"
        markerEnd="url(#arrow-olive)"
        opacity="0.75"
      />
      {/* Bottom-left arc (7:30): Reward → Review. */}
      <path
        d="M 265 460 A 135 135 0 0 1 170 365"
        fill="none"
        stroke="#3E6B37"
        strokeWidth="3"
        markerEnd="url(#arrow-olive)"
        opacity="0.75"
      />
      {/* Top-left arc (10:30): Review → Cue. */}
      <path
        d="M 170 295 A 135 135 0 0 1 265 200"
        fill="none"
        stroke="#3E6B37"
        strokeWidth="3"
        markerEnd="url(#arrow-olive)"
        opacity="0.75"
      />

      {/* Center: VISION — pre-set to "Financial Success" */}
      <circle
        cx="300" cy="330" r="55"
        fill="#C05C3D"
      />
      <text
        x="300" y="312"
        textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10"
        fontWeight="700"
        letterSpacing="3"
        fill="#F7F5F0"
        opacity="0.85"
      >
        VISION
      </text>
      <text
        x="300" y="336"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="20"
        fontWeight="600"
        fill="#FFFFFF"
      >
        Financial
      </text>
      <text
        x="300" y="358"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="20"
        fontWeight="600"
        fill="#FFFFFF"
      >
        Success
      </text>

      {/* CUE — 12 o'clock, above ring */}
      <text
        x="300" y="128"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="28"
        fontWeight="700"
        fill="#2C302E"
      >
        Cue
      </text>
      <text x="300" y="152" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {cueLines.line1}
      </text>
      <text x="300" y="165" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {cueLines.line2}
      </text>

      {/* ROUTINE — 3 o'clock, right of ring */}
      <text
        x="520" y="325"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="28"
        fontWeight="700"
        fill="#2C302E"
      >
        Routine
      </text>
      <text x="520" y="351" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {routineLines.line1}
      </text>
      <text x="520" y="364" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {routineLines.line2}
      </text>

      {/* REWARD — 6 o'clock, below ring */}
      <text
        x="300" y="497"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="28"
        fontWeight="700"
        fill="#2C302E"
      >
        Reward
      </text>
      <text x="300" y="523" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {rewardLines.line1}
      </text>
      <text x="300" y="536" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {rewardLines.line2}
      </text>

      {/* REVIEW — 9 o'clock, left of ring (the weekly check-in beat) */}
      <text
        x="80" y="325"
        textAnchor="middle"
        fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
        fontSize="28"
        fontWeight="700"
        fill="#2C302E"
      >
        Review
      </text>
      <text x="80" y="351" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {reviewLines.line1}
      </text>
      <text x="80" y="364" textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="10" fill="#4d4f4c">
        {reviewLines.line2}
      </text>

      {/* Footer */}
      <text
        x="300" y="580"
        textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontSize="9"
        letterSpacing="2"
        fill="#787671"
      >
        financialhabitstosucceed.com
      </text>
    </svg>
  );
});

export default HabitLoopDiagram;
